import customAxios from "./customAxios";

// ─── Types ──────────────────────────────────────────────────────
export interface BillItem {
  id_facture: number;
  total?: number;
  profit?: number;
  capital?: number;
  listofproducts?: any[];
  Userid?: number;
  created_at?: string;
}

export interface StatsData {
  totalselles: number;
  total: number;
  totalprofit: number;
  totalcapital: number;
}

export interface StatisticResponse {
  factures: BillItem[];
  total: number;
  totalprofit: number;
  totalcapital: number;
  totalselles: number;
}

export interface DateOption {
  label: string;
  value: string;
}

// ─── Date Helpers ───────────────────────────────────────────────

/** Format a Date to MM/DD/YYYY for the API */
export const formatIntervalDate = (date: Date): string =>
  `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
    .getDate()
    .toString()
    .padStart(2, "0")}/${date.getFullYear()}`;

/**
 * Returns { start, end } date range strings (MM/DD/YYYY) for common filters.
 * For "all", returns a very wide range (5 years back → today).
 * For "custom", pass customStart and customEnd.
 */
export const getDateRangeForFilter = (
  filter: string,
  customStart?: Date,
  customEnd?: Date,
): { start: string; end: string } => {
  const today = new Date();
  switch (filter) {
    case "today":
      return {
        start: formatIntervalDate(today),
        end: formatIntervalDate(today),
      };
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return {
        start: formatIntervalDate(y),
        end: formatIntervalDate(y),
      };
    }
    case "month": {
      const m = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        start: formatIntervalDate(m),
        end: formatIntervalDate(today),
      };
    }
    case "year": {
      const y2 = new Date(today.getFullYear(), 0, 1);
      return {
        start: formatIntervalDate(y2),
        end: formatIntervalDate(today),
      };
    }
    case "custom": {
      if (customStart && customEnd) {
        return {
          start: formatIntervalDate(customStart),
          end: formatIntervalDate(customEnd),
        };
      }
      // fallthrough to "all" if custom dates missing
    }
    case "all":
    default: {
      const fiveYearsAgo = new Date(
        today.getFullYear() - 5,
        today.getMonth(),
        today.getDate(),
      );
      return {
        start: formatIntervalDate(fiveYearsAgo),
        end: formatIntervalDate(today),
      };
    }
  }
};

// ─── API Call ───────────────────────────────────────────────────

/**
 * Fetch statistics using GET /statistic_intervalle?start=...&end=...
 * Always uses query params (never body), works on both web and React Native.
 */
export const fetchStatisticIntervalle = async (
  start: string,
  end: string,
): Promise<StatisticResponse> => {
  const response = await customAxios.get("/statistic_intervalle", {
    params: { start, end },
    withCredentials: true,
  });
  return response.data;
};
